/**
 * Coups de pouce — Attendu 1082 « Donner la probabilité sous diverses formes
 * (fraction, décimale, pourcentage) ».
 *
 * Entrée PARTAGÉE par le QCM et le relier (même geste : compter en fraction,
 * puis changer d'écriture). L'exemple (revue 2026-09-01) est HORS du vivier :
 * 7 jetons gagnants sur 100, dessinés en CARRÉ DES CENTIÈMES (grille 10 × 10,
 * représentation canonique du BO pour les fractions décimales — mémoire
 * cp-references-officielles) ; aucun dénominateur 100 n'est tirable par le
 * gen. SVG pas à pas piloté par les flèches de la sheet (data-etapes /
 * data-caption, mécanisme 2026-08-21).
 *
 * chaine_texte_trou : entrée dédiée (amener la fraction sur 100, roue à 20).
 * ecritures_tableau_vf : PAS d'entrée (pur rappel, doctrine gen_640) —
 * chaque ligne EST une connaissance sur les écritures, toute aide la donnerait.
 */

// Carré des centièmes : 100 cases, les 7 premières colorées (les cas qui
// conviennent) — construit par code pour rester lisible.
const GRILLE_CENTIEMES = (() => {
    let cases = '';
    for (let i = 0; i < 100; i++) {
        const x = (24 + (i % 10) * 11.5).toFixed(1);
        const y = (40 + Math.floor(i / 10) * 11.5).toFixed(1);
        const fill = i < 7 ? '#B45309' : '#ffffff';
        cases += `<rect x="${x}" y="${y}" width="10.5" height="10.5" fill="${fill}" stroke="#94a3b8" stroke-width="0.8"/>`;
    }
    return cases;
})();

const SVG_TROIS_ECRITURES = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 236" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Les trois écritures d'une probabilité, en 3 étapes à faire défiler avec les flèches. Une grille de 100 cases représente 100 jetons ; 7 cases sont colorées : 7 jetons gagnants. La fraction est 7 sur 100. 7 centièmes, c'est l'écriture décimale 0,07. Une fraction de dénominateur 100, c'est justement un pourcentage : 7 pour cent. Trois écritures, un même nombre">
  <style>
    @keyframes cp1082-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1082-pop { opacity: 0; animation: cp1082-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">7 jetons gagnants sur 100 : la fraction 7/100</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">7 centièmes : l'écriture décimale 0,07</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">La fraction de dénominateur 100 : 7 %</text>

  <!-- ── La grille des 100 jetons et la fraction (étape 1) ───────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${GRILLE_CENTIEMES}
    <text x="81" y="172" font-size="12" font-weight="600" fill="#475569" text-anchor="middle">100 jetons, 7 gagnants</text>
    <text x="250" y="82" font-size="20" font-weight="800" fill="#1E3A8A" text-anchor="middle">7/100</text>
  </g>

  <!-- ── L'écriture décimale (étape 2) ───────────────────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1082-pop">
      <text x="250" y="116" font-size="18" font-weight="800" fill="#1D9E75" text-anchor="middle">= 0,07</text>
    </g>
  </g>

  <!-- ── Le pourcentage (étape 3) ────────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1082-pop">
      <text x="250" y="150" font-size="18" font-weight="800" fill="#B45309" text-anchor="middle">= 7 %</text>
      <rect x="20" y="190" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="207" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Trois écritures, un même nombre.</text>
    </g>
  </g>

  <text x="180" y="233" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

/*
 * chaine_texte_trou : roue à 20 secteurs (3 gagnants) — les vingtièmes sont
 * BRIDÉS hors de la chaîne (revue 2026-09-01), l'exemple ne peut donc jamais
 * souffler une réponse. Le geste enseigné : amener la fraction sur 100.
 */
const ROUE_VINGT = (() => {
    const cx = 80; const cy = 108; const r = 56;
    let secteurs = '';
    for (let i = 0; i < 20; i++) {
        const a1 = ((i * 18 - 90) * Math.PI) / 180;
        const a2 = (((i + 1) * 18 - 90) * Math.PI) / 180;
        const x1 = (cx + r * Math.cos(a1)).toFixed(1);
        const y1 = (cy + r * Math.sin(a1)).toFixed(1);
        const x2 = (cx + r * Math.cos(a2)).toFixed(1);
        const y2 = (cy + r * Math.sin(a2)).toFixed(1);
        const fill = i < 3 ? '#B45309' : '#ffffff';
        secteurs += `<path d="M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2} Z" fill="${fill}" stroke="#94a3b8" stroke-width="1"/>`;
    }
    return secteurs;
})();

const SVG_SUR_CENT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 236" width="100%" style="max-width:380px;height:auto;display:block" role="img" data-etapes="3"
     aria-label="Amener une fraction sur 100, en 3 étapes à faire défiler avec les flèches. Une roue équilibrée est partagée en 20 secteurs égaux, 3 sont colorés : la fraction est 3 sur 20. Je multiplie en haut et en bas par 5 : 3 sur 20 égale 15 sur 100. Sur 100, tout se lit d'un coup : 0,15 en écriture décimale, et 15 pour cent. Change d'habit, pas de valeur">
  <style>
    @keyframes cp1082b-pop { from { opacity: 0; } to { opacity: 1; } }
    .cp1082b-pop { opacity: 0; animation: cp1082b-pop 0.5s ease forwards; }
  </style>
  <!-- sous-titres : un par étape (exclusifs, pilotés par la sheet) -->
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1E3A8A" opacity="1" data-caption="1" font-family="system-ui,sans-serif">3 secteurs gagnants sur 20 : la fraction 3/20</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#1D9E75" opacity="0" data-caption="2" font-family="system-ui,sans-serif">× 5 en haut et en bas : 3/20 = 15/100</text>
  <text x="8" y="21" font-size="12.5" font-weight="600" fill="#B45309" opacity="0" data-caption="3" font-family="system-ui,sans-serif">Sur 100, tout se lit : 0,15 et 15 %</text>

  <!-- ── La roue et la fraction (étape 1) ────────────────────────────── -->
  <g data-etape="1" font-family="system-ui,sans-serif">
    ${ROUE_VINGT}
    <text x="80" y="188" font-size="12" font-weight="600" fill="#475569" text-anchor="middle">20 secteurs, 3 gagnants</text>
    <text x="250" y="82" font-size="20" font-weight="800" fill="#1E3A8A" text-anchor="middle">3/20</text>
  </g>

  <!-- ── Le geste × 5 vers les centièmes (étape 2) ───────────────────── -->
  <g data-etape="2" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1082b-pop">
      <rect x="304" y="98" width="48" height="20" rx="8" fill="#ecfdf5" stroke="#1D9E75"/>
      <text x="328" y="112" font-size="12" font-weight="800" fill="#1D9E75" text-anchor="middle">× 5</text>
      <text x="240" y="116" font-size="18" font-weight="800" fill="#1D9E75" text-anchor="middle">= 15/100</text>
    </g>
  </g>

  <!-- ── La lecture sur 100 (étape 3) ────────────────────────────────── -->
  <g data-etape="3" data-anim="1" font-family="system-ui,sans-serif">
    <g class="cp1082b-pop">
      <text x="250" y="150" font-size="18" font-weight="800" fill="#B45309" text-anchor="middle">= 0,15 = 15 %</text>
      <rect x="20" y="190" width="320" height="26" rx="10" fill="#eff6ff" stroke="#bfdbfe"/>
      <text x="180" y="207" font-size="12" font-weight="800" fill="#1E3A8A" text-anchor="middle">Change d'habit, pas de valeur.</text>
    </g>
  </g>

  <text x="180" y="233" font-size="11" font-weight="700" fill="#b45309" text-anchor="middle" opacity="0" data-caption="1" font-family="system-ui,sans-serif">↓ Touche les flèches pour avancer, étape par étape ↓</text>
</svg>`.trim();

const AIDE_FORMES = {
    pages: [
        {
            titre: 'Compter, puis changer d\'écriture',
            lignes: [
                '**Compte d\'abord en fraction** : le nombre de cas qui conviennent, sur le nombre de cas en tout (7 jetons gagnants sur 100 jetons : 7/100).',
                'Les trois écritures désignent **le même nombre** : 7/100 = 0,07 = 7 %. Le pourcentage, c\'est la fraction de dénominateur 100.',
                'Impossible : 0, c\'est 0 %. Certain : 1, c\'est 100 %. Aucune probabilité ne dépasse 1.',
            ],
        },
        { titre: 'Un exemple : le carré des centièmes', svg: SVG_TROIS_ECRITURES },
    ],
};

const CP_1082 = {
    evenement_qcm_unique: AIDE_FORMES,
    proba_relier: AIDE_FORMES,
    chaine_texte_trou: {
        pages: [
            {
                titre: 'Amener la fraction sur 100',
                lignes: [
                    '**Amène ta fraction sur 100** : la moitié avec × 50, les quarts avec × 25, les dixièmes avec × 10 (exemple : 3/20, c\'est × 5, donc 15/100).',
                    '**Sur 100, tout se lit d\'un coup** : 15/100, c\'est 0,15 en écriture décimale, et c\'est 15 %.',
                    'Les trois écritures désignent le même nombre : tu changes d\'habit, pas de valeur.',
                ],
            },
            { titre: 'Un exemple : la roue à 20 secteurs', svg: SVG_SUR_CENT },
        ],
    },
    // ecritures_tableau_vf : volontairement ABSENT (pur rappel, cf. en-tête).
};

export default CP_1082;
